import { CURSOR_REJECTED_PROBLEM_TYPE } from '@misumi/shared'
import { useCallback, useMemo, useRef } from 'react'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { combinedItemCount } from '../../lib/cart/cart-count-context'
import type { CursorPage } from '../../lib/list/useCursorList'
import { CursorRejectedError, useCursorList } from '../../lib/list/useCursorList'
import { PartNotFoundError } from './types'
import type {
  LibraryBomRow,
  LibraryListing,
  LibraryListingQuery,
  LibraryPartRow,
  PartActivityEvent,
  PartDetail,
} from './types'

/**
 * UID-603 (ADR-0176 D6 row 6) — the Parts Library page size. `usePartsLibrary`
 * is "the natural and ONLY place to add `cursor`/`limit` on the web side" per
 * the ADR — the real service seam for `/parts` — and the shared cursor hook
 * (D1.4) sits directly on top of it.
 */
export const PARTS_LIBRARY_PAGE_SIZE = 50

/** Outcome of a reorder: the authoritative cart line-count for the top-bar badge. */
export interface ReorderResult {
  readonly cartLineCount: number
}

/**
 * The data-layer seam for the Parts Library + Part detail screens (UID-44,
 * UID-45). Shaped exactly against the frozen BFF contracts the
 * software-engineer handed off (types.ts's doc comments) — a future
 * BFF-backed implementation (calling `apiClient.GET('/api/parts', ...)` /
 * `GET('/api/parts/{partId}', ...)`) is a drop-in replacement for
 * `mockPartsLibraryService` (moved to `./test-fixtures.ts` — code-review
 * UID-106 hygiene finding: test doubles must not be reachable from a
 * runtime module); nothing else in this vertical changes.
 */
export interface PartsLibraryService {
  /**
   * UID-603 (ADR-0176 D1.2) — `limit`/`cursor` are OPTIONAL on the query:
   * omitting both preserves the pre-UID-603 unbounded behaviour byte-for-
   * byte, which is what `sku-resolver.ts` and `add-parts-search.ts` (D6 rows
   * 5/7) rely on — they call `listLibrary` directly with no `limit` and must
   * keep getting the full tenant library. `usePartsLibrary` (below) is the
   * only caller that ever passes `limit`, including for a `workspaceId`
   * query (`WorkspacePage.tsx`) — the backend's `?workspaceId` + `limit`
   * combination is a real, correctly paginated, workspace-scoped read (the
   * concurrent C2 fix landed this properly rather than 400-guarding it), so
   * there is no special-cased fallback here.
   */
  listLibrary(query: LibraryListingQuery): Promise<LibraryListing>
  addPartsToRfq(sourceableIds: readonly string[]): Promise<void>
  /** Rejects with `PartNotFoundError` for an unknown/cross-tenant id — the
   * contract's "tenant-scoped 404" (types.ts). The resolved `PartDetail`
   * carries its own `activity` feed (UID-236) — there is no separate
   * activity call. */
  getPartDetail(partId: string): Promise<PartDetail>
  /** Reorder a previously-purchased part (UID-65): adds it to the cart carrying its
   * prior config + order quantity, and resolves with the updated cart line-count for
   * the top-bar badge. Rejects with an `Error` (message ready for a toast) on failure. */
  reorder(sourceableId: string): Promise<ReorderResult>
  /** UID-422 "Remove from library": soft-deletes the caller's own part (server
   * tombstones it — see `removePart` on the API side). Rejects with an `Error`
   * (message ready for a toast) on failure, including a repeat call against
   * an already-removed part (the server 404s; that surfaces here as an error
   * rather than a silent success). */
  removePart(partId: string): Promise<void>
}

/** Pull the `title` off a Problem+JSON error body for a user-facing message.
 * Exported so sibling BFF-backed services in this vertical (`useBomDetail.ts`)
 * reuse the same Problem+JSON → message mapping rather than re-implementing it. */
export function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/** Whether a Problem+JSON error body's `type` matches the given RFC 7807
 * discriminator. Exported so `rfq-list-service.ts`'s `bffListRfqs` reuses
 * the same check rather than re-implementing it (review W4, fifth pass). */
export function isProblemType(error: unknown, type: string): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    (error as { type?: unknown }).type === type
  )
}

/**
 * REAL BFF-backed Parts Library service (UID-65). Reorder must POST a part id the
 * backend library actually holds, so the listing/detail are read from the BFF too
 * (the mock's `part-1…5` ids don't exist server-side → the reorder 404'd). The FE
 * `types.ts` shapes are verbatim mirrors of the frozen `GET /api/parts` contract
 * (see that file's doc comment), so the generated response is the same shape — the
 * narrow cast at each boundary is the intended "signature-compatible drop-in", not a
 * reshape. `authMiddleware` attaches the session cookie + CSRF token automatically.
 */
async function bffListLibrary(query: LibraryListingQuery): Promise<LibraryListing> {
  const { data, error, response } = await apiClient.GET('/parts', {
    params: {
      query: {
        ...(query.q !== undefined ? { q: query.q } : {}),
        ...(query.status !== undefined ? { status: query.status } : {}),
        ...(query.sort !== undefined ? { sort: query.sort } : {}),
        ...(query.groupBySession !== undefined ? { groupBySession: query.groupBySession } : {}),
        ...(query.workspaceId !== undefined ? { workspaceId: query.workspaceId } : {}),
        // UID-603 (ADR-0176 D1.2) — omitted entirely (not sent as `undefined`)
        // when absent, so a caller that never passes these (sku-resolver.ts,
        // add-parts-search.ts, and every other whole-library reader, D6/D7.3)
        // gets the exact pre-UID-603 request byte-for-byte.
        ...(query.limit !== undefined ? { limit: query.limit } : {}),
        ...(query.cursor !== undefined ? { cursor: query.cursor } : {}),
      },
    },
  })
  if (error || !data) {
    const message = problemTitle(error, 'We couldn’t load your parts library. Try again.')
    // M2 (code-review UID-603 fourth pass) — same posture as
    // `rfq-list-service.ts`'s `bffListRfqs`: a cursor-carrying request that
    // 400s is `library-store.ts`'s deliberate "cursor doesn't resolve"
    // rejection, never retriable by re-issuing the same cursor via
    // `loadMore()`.
    //
    // Review W4 (fifth pass) — gated on the Problem `type` discriminator
    // (`CURSOR_REJECTED_PROBLEM_TYPE`, `@misumi/shared`), not just "cursor
    // present + 400": an unrelated 400 that happens to arrive on a
    // cursor-carrying request (a validation failure, a workspaceId+limit
    // refusal on the RFQ side) must not be mistaken for a dead cursor. A
    // message-substring check was rejected — several genuine rejections
    // ("malformed anchor id", "empty token") don't contain the word
    // "cursor" at all, so text-matching would break real cases instead of
    // just filtering false positives (see the constant's doc comment).
    if (
      query.cursor !== undefined &&
      response.status === 400 &&
      isProblemType(error, CURSOR_REJECTED_PROBLEM_TYPE)
    ) {
      throw new CursorRejectedError(message)
    }
    throw new Error(message)
  }
  return data as unknown as LibraryListing
}

/** Normalizes the generated `PartActivityEvent` (contract field `message`)
 * into this vertical's `PartActivityEvent` view model (`label`) — this
 * repo's "normalize generated type into a feature view model" convention
 * (web-conventions). `actorKind` passes through unchanged for badging. */
function toFeedEvent(event: components['schemas']['PartActivityEvent']): PartActivityEvent {
  return {
    id: event.id,
    kind: event.kind,
    label: event.message,
    occurredAt: event.occurredAt,
    ...(event.actor != null ? { actor: event.actor } : {}),
    ...(event.actorKind !== undefined ? { actorKind: event.actorKind } : {}),
  }
}

async function bffGetPartDetail(partId: string): Promise<PartDetail> {
  const { data, error, response } = await apiClient.GET('/parts/{partId}', {
    params: { path: { partId } },
  })
  if (response.status === 404) {
    throw new PartNotFoundError(partId)
  }
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this part. Try again.'))
  }
  const detail = data as unknown as PartDetail & {
    readonly activityLog?: readonly components['schemas']['PartActivityEvent'][]
  }
  // `activityLog` is newest-first already (contract) — no client re-sort. The
  // UID-332 (ADR-0113) additive fields — `revisionGroups`, `activeQuotes`,
  // `rfqChips`, `quotedPricing`, `description` — are pass-through (no view-model
  // normalization needed), carried straight through the spread onto PartDetail.
  return { ...detail, activity: (detail.activityLog ?? []).map(toFeedEvent) }
}

async function bffAddPartsToRfq(sourceableIds: readonly string[]): Promise<void> {
  const { error } = await apiClient.POST('/parts/rfq', {
    body: { partIds: [...sourceableIds] },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t add these parts to an RFQ. Try again.'))
  }
}

/**
 * The REAL reorder call (UID-65). POSTs the reorder seam, then reads the authoritative
 * cart line-count back so the caller can refresh the top-bar badge.
 */
async function reorderViaBff(sourceableId: string): Promise<ReorderResult> {
  const { error } = await apiClient.POST('/parts/{partId}/reorder', {
    params: { path: { partId: sourceableId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t add this part to your cart. Try again.'))
  }
  // ReorderAck doesn't carry the cart, so read the authoritative line-count for the badge.
  // UID-423/ADR-0127: the badge is a COMBINED count (part lines + requested
  // services) everywhere it's pushed, so it never contradicts itself between
  // screens — the full CartView is available here, so combine it.
  const { data } = await apiClient.GET('/cart')
  return { cartLineCount: combinedItemCount(data) }
}

/** The REAL remove call (UID-422). DELETE has no response body — a 204 resolves,
 * anything else (404 unknown/already-removed, 401/403) rejects with a toast-ready
 * message via the same Problem+JSON convention every other mutation here uses. */
async function removePartViaBff(partId: string): Promise<void> {
  const { error } = await apiClient.DELETE('/parts/{partId}', {
    params: { path: { partId } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'We couldn’t remove this part. Try again.'))
  }
}

/**
 * The service the running app uses (UID-106): fully BFF-backed for listing, detail,
 * RFQ, and reorder (UID-65) so the rows carry REAL part ids the reorder seam accepts.
 * `getPartDetail` (UID-236) sources the "Part activity" tab's feed directly from the
 * same response's `activityLog` — there is no separate activity call. Tests inject
 * their own service (often `mockPartsLibraryService`, from `mock-data.ts` fixtures),
 * so they never hit the network.
 */
export const defaultPartsLibraryService: PartsLibraryService = {
  listLibrary: bffListLibrary,
  getPartDetail: bffGetPartDetail,
  addPartsToRfq: bffAddPartsToRfq,
  reorder: reorderViaBff,
  removePart: removePartViaBff,
}

export type PartsLibraryState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly listing: LibraryListing }

export interface UsePartsLibraryResult {
  readonly state: PartsLibraryState
  readonly refetch: () => void
  readonly addPartsToRfq: (sourceableIds: readonly string[]) => Promise<void>
  readonly reorder: (sourceableId: string) => Promise<ReorderResult>
  /** UID-422 "Remove from library": removes the part, then refetches (reset to
   * page 1, mirroring `addPartsToRfq`) so the row disappears from the listing
   * on success. Rejects (propagating the service's error) on failure, leaving
   * the listing untouched — the caller surfaces its own error toast. */
  readonly removePart: (partId: string) => Promise<void>
  /** UID-603 (ADR-0176 D1.4) — advances the `parts` collection via the last
   * page's `nextCursor`, APPENDING onto `state.listing.parts` (deduped by
   * `sourceable.id`, D1.5). `boms`/`counts` are never paginated (D7.1) and
   * simply reflect whatever the latest page sent. No-op if there's no next
   * page, a load-more is already in flight, or nothing has loaded yet. */
  readonly loadMore: () => void
  /** True while a "load more" request is in flight — the list stays on
   * screen; this never regresses to the full-page `loading` skeleton. */
  readonly isLoadingMore: boolean
  /** Set when a "load more" request fails; the already-loaded rows stay put. */
  readonly loadMoreError: string | null
  /** M2 (code-review UID-603 fourth pass) — true when `loadMoreError` came
   * from a `CursorRejectedError` (the current `nextCursor` is dead — e.g.
   * `library-store.ts`'s deliberate cursor-doesn't-resolve 400 — not a
   * transient network/500 failure). A retry affordance should call
   * `refetch()` (reset to page 1, which mints a fresh cursor), not
   * `loadMore()` (which would just re-issue the same dead cursor forever),
   * when this is true. Always `false` when `loadMoreError` is `null`. */
  readonly loadMoreErrorIsCursorRejection: boolean
}

/** The shape `useCursorList` operates over for `/parts`: `items` is the
 * generic name the shared hook merges/dedupes on; `boms`/`counts` ride along
 * unpaginated (D7.1) and are simply carried through on every page. */
interface PartsCursorPage extends CursorPage<LibraryPartRow> {
  // L5 — optional (not defaulted here) so a later page that omits either
  // field leaves the KEY ITSELF absent on the merged page object, letting
  // `useCursorList`'s "prior page wins if the new page is silent" spread
  // order actually preserve the earlier page's real value instead of
  // clobbering it with `undefined`. Page 1 always sends both (see
  // `fetchPage` above), so in practice these are defined by the time
  // anything downstream reads them — the `state` derivation below still
  // falls back defensively.
  readonly boms?: readonly LibraryBomRow[]
  readonly counts?: { readonly parts: number; readonly boms: number }
}

/**
 * Loads the Parts Library through a `PartsLibraryService` (defaults to the
 * REAL BFF-backed `defaultPartsLibraryService`, per every sibling hook in
 * this vertical — code-review UID-106 W3; the mock is still available for
 * tests to inject explicitly), re-fetching whenever the query
 * (search/status/sort/group-by-session) changes — the BFF owns filtering,
 * so the screen never filters client-side.
 *
 * UID-603 (ADR-0176) — pages the `parts` collection at `PARTS_LIBRARY_PAGE_SIZE`
 * through the shared `useCursorList` hook (D1.4), the same one `RfqListPage`
 * consumes. A query change (search/status/sort/group-by-session/project)
 * resets back to page 1, exactly like the pre-UID-603 refetch did.
 */
export function usePartsLibrary(
  query: LibraryListingQuery,
  service: PartsLibraryService = defaultPartsLibraryService,
): UsePartsLibraryResult {
  const serviceRef = useRef(service)
  serviceRef.current = service

  const { q, status, sort, groupBySession, workspaceId } = query

  const fetchPage = useCallback(
    async (opts: {
      readonly cursor?: string
      readonly limit: number
    }): Promise<PartsCursorPage> => {
      const listing = await serviceRef.current.listLibrary({
        ...(q !== undefined ? { q } : {}),
        ...(status !== undefined ? { status } : {}),
        ...(sort !== undefined ? { sort } : {}),
        ...(groupBySession !== undefined ? { groupBySession } : {}),
        ...(workspaceId !== undefined ? { workspaceId } : {}),
        // UID-603 (ADR-0176 D1.2) — omitted entirely (not sent as
        // `undefined`) when absent, so a caller that never passes these
        // (sku-resolver.ts, add-parts-search.ts, and every other whole-
        // library reader, D6/D7.3) gets the exact pre-UID-603 request
        // byte-for-byte. Sent unconditionally otherwise, including for a
        // `workspaceId` query (`WorkspacePage.tsx`) — the backend's
        // `?workspaceId` + `limit` combination is a real, correctly
        // paginated, workspace-scoped read (the concurrent C2 fix), not a
        // 400-guarded one, so there is no special case here.
        limit: opts.limit,
        ...(opts.cursor !== undefined ? { cursor: opts.cursor } : {}),
      })
      // M3 (code-review UID-603 fourth pass) — `listing.nextCursor`'s
      // ABSENCE means "this response was not paginated" per `types.ts`'s
      // `LibraryListing` doc comment, distinct from an explicit `null`
      // ("paginated, and this is the last page"). In principle a
      // cursor-bearing (load-more) call that comes back with the field
      // omitted altogether is ambiguous rather than "end of list" — but
      // that distinction is NOT one every `PartsLibraryService`
      // implementation in this codebase actually honors: several existing
      // fixtures (this vertical's own tests) signal "no more pages" on a
      // load-more response by omitting `nextCursor` entirely rather than
      // setting it to `null`. Trying to enforce the stricter reading here
      // would treat that established, working convention as a thrown
      // error. So this stays a plain coalesce — the fix is documented as a
      // known gap rather than silently forgotten: a real backend
      // (`defaultPartsLibraryService`) must always send an explicit
      // `nextCursor` (`null` or a string) on every limit-bearing response,
      // which is what actually prevents silent truncation in production;
      // this coalesce is only reachable for a non-conforming service.
      return {
        items: listing.parts,
        nextCursor: listing.nextCursor ?? null,
        ...(listing.totalCount !== undefined ? { totalCount: listing.totalCount } : {}),
        // L5 (code-review UID-603 fourth pass) — omitted (not sent as
        // `undefined`) when the response doesn't carry it, so
        // `useCursorList`'s merge (`{ ...latest.page, ...newPage }`) leaves
        // an EARLIER page's real `boms`/`counts` alone instead of clobbering
        // them with `undefined` — a later page is only ever guaranteed to
        // resend page-independent fields when `cursor` is absent (D4/D5);
        // omitting the key entirely (rather than setting it to `undefined`)
        // is what makes the spread's "prior page wins if the new page is
        // silent" behavior actually work.
        ...(listing.boms !== undefined ? { boms: listing.boms } : {}),
        ...(listing.counts !== undefined ? { counts: listing.counts } : {}),
      }
    },
    [q, status, sort, groupBySession, workspaceId],
  )

  const getId = useCallback((row: LibraryPartRow) => row.sourceable.id, [])

  const {
    state: cursorState,
    loadMore,
    reload,
  } = useCursorList<LibraryPartRow, PartsCursorPage>(fetchPage, {
    getId,
    pageSize: PARTS_LIBRARY_PAGE_SIZE,
    enabled: true,
    resetKey: [q, status, sort, groupBySession, workspaceId],
  })

  const state: PartsLibraryState = useMemo(() => {
    if (cursorState.status === 'ready') {
      return {
        status: 'ready',
        listing: {
          parts: cursorState.page.items,
          // L5 — defensive fallback only; page 1 always sends both (see
          // `fetchPage`'s doc comment above), so these are always defined in
          // practice by the time anything is 'ready'.
          boms: cursorState.page.boms ?? [],
          counts: cursorState.page.counts ?? { parts: 0, boms: 0 },
          nextCursor: cursorState.page.nextCursor,
          ...(cursorState.page.totalCount !== undefined
            ? { totalCount: cursorState.page.totalCount }
            : {}),
        },
      }
    }
    if (cursorState.status === 'error') return { status: 'error', message: cursorState.message }
    return { status: 'loading' }
  }, [cursorState])

  const addPartsToRfq = useCallback(
    async (sourceableIds: readonly string[]) => {
      await serviceRef.current.addPartsToRfq(sourceableIds)
      reload()
    },
    [reload],
  )

  // Reorder doesn't change the listing, so it does NOT refetch — the caller updates
  // the cart-count badge from the returned line-count and surfaces its own toast.
  const reorder = useCallback(
    (sourceableId: string) => serviceRef.current.reorder(sourceableId),
    [],
  )

  const removePart = useCallback(
    async (partId: string) => {
      await serviceRef.current.removePart(partId)
      reload()
    },
    [reload],
  )

  return {
    state,
    refetch: reload,
    addPartsToRfq,
    reorder,
    removePart,
    loadMore,
    isLoadingMore: cursorState.status === 'ready' && cursorState.isLoadingMore,
    loadMoreError: cursorState.status === 'ready' ? cursorState.loadMoreError : null,
    loadMoreErrorIsCursorRejection:
      cursorState.status === 'ready' && cursorState.loadMoreErrorIsCursorRejection,
  }
}
